import type { ReactNode } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import { translate } from '../../common/helpers/translate.helper';
import type { registerDrawerNavigationGuard } from '../../navigation/navigationHistory';

import { DRAWER_TRANSITION_DURATION_MS } from './drawer.constants';
import type { DrawerGuard } from './DrawerContext';
import { DrawerContext } from './DrawerContext';
import type { DrawerConfirmationOptions, Layer, LayerAction, LayerStoreState } from './layerStoreReducer';
import { layerStoreReducer } from './layerStoreReducer';
import { LayerActionType } from './types/LayerActionType';
import { LayerStatus } from './types/LayerStatus';

interface DrawerContextProviderProps {
    children: ReactNode;
    registerNavigationGuard?: typeof registerDrawerNavigationGuard;
}

export const DrawerContextProvider = ({ children, registerNavigationGuard }: DrawerContextProviderProps) => {
    const [store, setStore] = useState<LayerStoreState>({ layers: [] });
    const [busyLayerIds, setBusyLayerIds] = useState<ReadonlySet<number>>(new Set());
    const storeRef = useRef(store);
    const isMounted = useRef(true);
    const nextId = useRef(0);
    const navigationClosePending = useRef(false);
    const guards = useRef(new Map<number, DrawerGuard>());
    const confirmations = useRef(new Map<number, (confirmed: boolean) => void>());
    const closingTimers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

    const dispatch = useCallback((action: LayerAction) => {
        if (!isMounted.current) {
            return;
        }
        storeRef.current = layerStoreReducer(storeRef.current, action);
        setStore(storeRef.current);
    }, []);

    useEffect(() => {
        isMounted.current = true;
        const timers = closingTimers.current;
        const pendingConfirmations = confirmations.current;
        const registeredGuards = guards.current;
        return () => {
            isMounted.current = false;
            timers.forEach(clearTimeout);
            pendingConfirmations.forEach((resolve) => resolve(false));
            timers.clear();
            pendingConfirmations.clear();
            registeredGuards.clear();
        };
    }, []);

    const closeLayer = useCallback(
        (id: number, navigation = false) => {
            const layer = storeRef.current.layers.find((candidate) => candidate.id === id);
            if (!isMounted.current || !layer || layer.status === LayerStatus.CLOSING) {
                return;
            }
            if (navigation) {
                flushSync(() => dispatch({ type: LayerActionType.CLOSE, id }));
            } else {
                dispatch({ type: LayerActionType.CLOSE, id });
            }
            confirmations.current.get(id)?.(false);
            confirmations.current.delete(id);
            closingTimers.current.set(
                id,
                setTimeout(() => {
                    dispatch({ type: LayerActionType.REMOVE, id });
                    closingTimers.current.delete(id);
                    guards.current.delete(id);
                    setBusyLayerIds((current) => {
                        if (!current.has(id)) {
                            return current;
                        }
                        const next = new Set(current);
                        next.delete(id);
                        return next;
                    });
                }, DRAWER_TRANSITION_DURATION_MS)
            );
            layer.onPop?.();
        },
        [dispatch]
    );

    const pop = useCallback(
        (id?: number) => {
            const layerId = id ?? storeRef.current.layers.at(-1)?.id;
            if (layerId !== undefined) {
                closeLayer(layerId);
            }
        },
        [closeLayer]
    );

    const confirm = useCallback(
        (options: DrawerConfirmationOptions, id?: number): Promise<boolean> => {
            const layer = storeRef.current.layers.findLast((layer) => layer.status !== LayerStatus.CLOSING);
            if (
                !isMounted.current ||
                !layer ||
                (id !== undefined && layer.id !== id) ||
                layer.status !== LayerStatus.OPEN
            ) {
                return Promise.resolve(false);
            }
            return new Promise((resolve) => {
                confirmations.current.set(layer.id, resolve);
                dispatch({ type: LayerActionType.CONFIRM, id: layer.id, confirmation: options });
            });
        },
        [dispatch]
    );

    const resolveConfirmation = useCallback(
        (id: number, confirmed: boolean) => {
            const layer = storeRef.current.layers.findLast((layer) => layer.status !== LayerStatus.CLOSING);
            if (!isMounted.current || layer?.id !== id || layer.status !== LayerStatus.CONFIRMING) {
                return;
            }
            const resolve = confirmations.current.get(id);
            confirmations.current.delete(id);
            dispatch({ type: LayerActionType.CLEAR_CONFIRMATION, id });
            resolve?.(confirmed);
        },
        [dispatch]
    );

    const requestClose = useCallback(
        (id?: number, onClose?: () => void) => {
            if (!isMounted.current) {
                return;
            }
            const layer = storeRef.current.layers.findLast((layer) => layer.status !== LayerStatus.CLOSING);
            if (!layer) {
                if (id === undefined) {
                    onClose?.();
                }
                return;
            }
            if ((id !== undefined && layer.id !== id) || layer.status !== LayerStatus.OPEN) {
                return;
            }
            const guard = guards.current.get(layer.id);
            if (guard?.isBusy) {
                return;
            }
            const completeClose = () => {
                if (onClose) {
                    onClose();
                } else {
                    closeLayer(layer.id);
                }
            };
            if (!guard?.hasUnsavedChanges) {
                completeClose();
                return;
            }
            void confirm(
                {
                    title: translate('drawer.discardUnsavedChanges'),
                    description: translate('drawer.unsavedChangesWillBeLost'),
                    confirmText: translate('drawer.discardChanges'),
                    destructive: true,
                },
                layer.id
            ).then((confirmed) => {
                const currentLayer = storeRef.current.layers.findLast((layer) => layer.status !== LayerStatus.CLOSING);
                if (
                    confirmed &&
                    isMounted.current &&
                    currentLayer?.id === layer.id &&
                    currentLayer.status === LayerStatus.OPEN &&
                    !guards.current.get(layer.id)?.isBusy
                ) {
                    completeClose();
                }
            });
        },
        [closeLayer, confirm]
    );

    const requestCloseAll = useCallback((): boolean => {
        if (!isMounted.current || navigationClosePending.current || confirmations.current.size) {
            return false;
        }
        const layers = storeRef.current.layers.filter((layer) => layer.status !== LayerStatus.CLOSING).reverse();
        if (layers.some((layer) => layer.status !== LayerStatus.OPEN || guards.current.get(layer.id)?.isBusy)) {
            return false;
        }
        navigationClosePending.current = true;
        const pushVersion = nextId.current;
        try {
            for (const layer of layers) {
                if (
                    guards.current.get(layer.id)?.hasUnsavedChanges &&
                    !window.confirm(translate('drawer.discardUnsavedChanges'))
                ) {
                    return false;
                }
                closeLayer(layer.id, true);
                if (!isMounted.current || nextId.current !== pushVersion) {
                    return false;
                }
            }
            return true;
        } finally {
            navigationClosePending.current = false;
        }
    }, [closeLayer]);

    useLayoutEffect(
        () =>
            registerNavigationGuard?.({
                requestCloseAll,
                shouldBlockUnload: () =>
                    storeRef.current.layers.some((layer) => {
                        const guard = guards.current.get(layer.id);
                        return layer.status !== LayerStatus.CLOSING && !!(guard?.hasUnsavedChanges || guard?.isBusy);
                    }),
            }),
        [registerNavigationGuard, requestCloseAll]
    );

    const push = useCallback(
        (layer: Layer) => {
            const id = ++nextId.current;
            dispatch({ type: LayerActionType.PUSH, layer: { ...layer, id, status: LayerStatus.OPEN } });
            return id;
        },
        [dispatch]
    );

    const registerGuard = useCallback((id: number, guard: DrawerGuard) => {
        if (guards.current.has(id)) {
            throw new Error(
                'Each drawer must have a single guard owner. Use DrawerContentLayout or useDrawerGuard, not both.'
            );
        }
        guards.current.set(id, guard);
        setBusyLayerIds((current) => {
            if (current.has(id) === !!guard.isBusy) {
                return current;
            }
            const next = new Set(current);
            if (guard.isBusy) {
                next.add(id);
            } else {
                next.delete(id);
            }
            return next;
        });
        return () => {
            if (guards.current.get(id) === guard) {
                guards.current.delete(id);
                setBusyLayerIds((current) => {
                    if (!current.has(id)) {
                        return current;
                    }
                    const next = new Set(current);
                    next.delete(id);
                    return next;
                });
            }
        };
    }, []);

    return (
        <DrawerContext.Provider
            value={{
                store,
                busyLayerIds,
                push,
                pop,
                requestClose,
                confirm,
                resolveConfirmation,
                registerGuard,
            }}
        >
            {children}
        </DrawerContext.Provider>
    );
};
